import {
    CLINIC_SUBSCRIPTION_PATH,
    isBillingActivationPending,
} from "@/domains/multi/clinicMemberPaths";
import { ClinicRoles } from "@/domains/multi/clinicRoles";
import type { IUser } from "@/domains/shared/models/IUser";
import { Roles } from "@/domains/shared/models/roles";
import {
    SECRETARY_HOME_PATH,
    SECRETARY_PROFILE_PATH,
    THERAPIST_BILLING_SETTINGS_PATH,
    THERAPIST_PROFILE_PATH,
    isBillingOnlyAccess,
    requiresPaymentBeforeAccess,
} from "@/domains/subscription/accountAccess";
import { isPaidPlan } from "@/domains/subscription/plans";

export const ADMIN_THERAPISTS_PATH = "/admin/terapeutas";
export const THERAPIST_HOME_PATH = "/profissional";
export const THERAPIST_FINANCE_PATH = "/profissional/financeiro";
export const PARENT_HOME_PATH = "/familia";
export const PARENT_PROFILE_PATH = "/familia/perfil";
export const VERIFY_EMAIL_PATH = "/verificar-email";

/** Profile routes reachable before e-mail OTP (auth/me PATCH is allowed). */
export const EMAIL_VERIFICATION_ALLOWED_PATHS = new Set([
    PARENT_PROFILE_PATH,
    THERAPIST_PROFILE_PATH,
    SECRETARY_PROFILE_PATH,
]);

export function needsEmailVerification(user: IUser | null | undefined): boolean {
    if (!user || user.role === Roles.Admin) {
        return false;
    }

    return user.emailVerified !== true;
}

export function isEmailVerificationAllowedPath(pathname: string): boolean {
    const trimmed = pathname.replace(/\/+$/, "");
    return EMAIL_VERIFICATION_ALLOWED_PATHS.has(trimmed);
}

/** First landing after public `/cadastro` signup — perfil for all therapist roles. */
export function postSignupProfilePath(user: IUser): string {
    if (user.role === Roles.Parent) {
        return PARENT_PROFILE_PATH;
    }

    return THERAPIST_PROFILE_PATH;
}

export function postAuthHomePath(user: IUser): string {
    if (user.role === Roles.Admin) {
        return ADMIN_THERAPISTS_PATH;
    }

    if (user.activeClinicId && user.clinicRoles?.includes(ClinicRoles.Finance) && !user.clinicRoles.includes(ClinicRoles.Therapist)) {
        return THERAPIST_FINANCE_PATH;
    }

    if (user.activeClinicId && user.clinicRoles?.includes(ClinicRoles.Manager)) {
        if (isBillingActivationPending(user)) {
            return `${CLINIC_SUBSCRIPTION_PATH}?checkout=1`;
        }
        return THERAPIST_HOME_PATH;
    }

    if (user.role === Roles.Therapist && requiresPaymentBeforeAccess(user)) {
        return CLINIC_SUBSCRIPTION_PATH;
    }

    // Legacy only: pending Pro intent without an active unpaid Pro trial (Basico shell).
    if (
        user.role === Roles.Therapist
        && isPaidPlan(user.pendingPaidPlanId)
        && (user.trialDaysRemaining == null || user.trialDaysRemaining <= 0)
    ) {
        return CLINIC_SUBSCRIPTION_PATH;
    }

    if (user.role === Roles.Therapist) {
        return THERAPIST_HOME_PATH;
    }

    if (user.role === Roles.Secretary) {
        return SECRETARY_HOME_PATH;
    }

    if (user.role === Roles.Parent) {
        return PARENT_HOME_PATH;
    }

    if (needsEmailVerification(user)) {
        return VERIFY_EMAIL_PATH;
    }

    if (user.role === Roles.Therapist && isBillingOnlyAccess(user.accountAccessMode)) {
        return THERAPIST_BILLING_SETTINGS_PATH;
    }

    return PARENT_HOME_PATH;
}
